import type { ReactNode } from 'react';
import './DataTable.css';

export interface Column<Row> {
  key: string;
  header: ReactNode;
  /** Right-align numeric columns and render them with tabular figures. */
  numeric?: boolean;
  render: (row: Row) => ReactNode;
}

export interface DataTableProps<Row> {
  caption: string;
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row, index: number) => string;
  /** Shown instead of the table when there are no rows. */
  empty?: ReactNode;
}

/**
 * Tabular data.
 *
 * The caption names the table for screen readers, and the whole thing scrolls
 * horizontally inside its own container so a wide dataset never forces the page
 * sideways.
 */
export function DataTable<Row>({ caption, columns, rows, rowKey, empty }: DataTableProps<Row>) {
  if (!rows.length && empty) {
    return <>{empty}</>;
  }

  return (
    <div className="sc-table__scroll" tabIndex={0} role="region" aria-label={caption}>
      <table className="sc-table">
        <caption className="sc-visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} scope="col" className={column.numeric ? 'sc-table__num' : ''}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={rowKey(row, index)}>
              {columns.map((column) => (
                <td key={column.key} className={column.numeric ? 'sc-table__num' : ''}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
